import { shallowEqual, useSelector } from 'react-redux';
import { WidgetType } from 'widgets/WidgetType';
import State from 'store/State';
import { LayoutState } from 'layout/LayoutState';
import { visit } from 'layout/store/reducer';
import { LayoutComponentShape, LayoutContainerShape } from 'layout/LayoutShape';
import { WidgetInfo } from 'widgets/WidgetInfo';
import { selectTilesState } from 'widgets/tiles/store/selectors';

export const selectLayout = (state: State) => state.layout?.result;

export const selectLayoutPanelId = (state: State) => state.layout.panelId;

export const selectPendingLayout = (state: State) => state.layout.pendingLayout;
export const selectLayoutManualSave = (state: State) => state.layout.manualSave;
export const selectIsDirty = (state: State) => state.layout.isDirty;

export const useSelectPendingLayout = () => useSelector(selectPendingLayout);
export const useSelectLayoutManualSave = () =>
    useSelector(selectLayoutManualSave);
export const useSelectIsLayoutDirty = () => useSelector(selectIsDirty);

export const useLayoutSelector = () =>
    useSelector((state: State) => selectLayout(state));

export const useLayoutStatus = () =>
    useSelector((state: State) => state.layout.status);

export const selectLayoutComponents: (state: State) => WidgetInfo[] = (
    state
) => {
    const layout = selectLayout(state);
    if (!layout?.components) {
        return [];
    }

    const result: WidgetInfo[] = [];
    for (const component of layout.components) {
        result.push(component);
        result.push(...getChildComponents(state, component));
    }

    return result;
};

export const useLayoutComponents = () => useSelector(selectLayoutComponents);

export const selectComponentKeysByType: (
    state: State,
    layoutType: WidgetType
) => string[] = (state, layoutType) => {
    return selectLayoutComponents(state)
        .filter((l) => l.type === layoutType)
        .map((l) => l.id);
};

export const selectActiveInputWidgetIdFromLayoutState = (
    layoutState: LayoutState
) => {
    return layoutState.result ? layoutState.result.activeInputId : undefined;
};

export const selectActiveInputWidgetIdFromLayout = (state: State) => {
    return selectActiveInputWidgetIdFromLayoutState(state.layout);
};

export const selectActiveInputWidget = (state: State) => {
    const activeInputId = selectActiveInputWidgetIdFromLayout(state);
    if (!activeInputId) {
        return undefined;
    }

    const components = selectLayoutComponents(state);
    if (components.length === 0) {
        return undefined;
    }

    const component = components.find((c) => c.id === activeInputId);
    if (!component) {
        return undefined;
    }
    return {
        id: activeInputId,
        component,
    };
};

export const useSelectActiveInputWidget = () => {
    return useSelector(selectActiveInputWidget, shallowEqual);
};

export const selectActiveWidgetIds: (state: State) => string[] = (state) =>
    selectWidgetIds(state, true);

export const selectAllWidgetIds: (state: State) => string[] = (state) =>
    selectWidgetIds(state, false);

function selectWidgetIds(state: State, activeOnly: boolean): string[] {
    return selectWidgets(state, activeOnly).map((w) => w.id);
}

export function selectWidgets(state: State, activeOnly: boolean): WidgetInfo[] {
    const layout = selectLayout(state);

    const ret: WidgetInfo[] = [];

    const addComponent = (id: string, type: WidgetType) => {
        ret.push({ id, type });
        ret.push(
            ...getChildComponents(state, {
                id,
                type,
            })
        );
    };

    if (layout?.shape === undefined) {
        if (layout?.components) {
            for (const component of layout.components) {
                addComponent(component.id, component.type);
            }
        }
        return ret;
    }

    visit(layout.shape, (c) => {
        if (c.type === 'stack') {
            const stack = c as LayoutContainerShape;
            for (const child of c.content) {
                const includeChild =
                    !activeOnly ||
                    c.content.indexOf(child) === stack.activeItemIndex;
                if (includeChild) {
                    const component = child as LayoutComponentShape;
                    ret.push({
                        id: component.componentId,
                        type: component.componentType,
                    });
                    ret.push(
                        ...getChildComponents(state, {
                            id: component.componentId,
                            type: component.componentType,
                        })
                    );
                }
            }
        }
    });
    return ret;
}

function getChildComponents(state: State, component: WidgetInfo): WidgetInfo[] {
    if (component.type === WidgetType.TILES) {
        const tilesState = selectTilesState(state, component.id);
        if (tilesState?.config?.tiles) {
            return tilesState.config.tiles.map((t) => ({
                id: t.widgetKey,
                type: t.widgetType,
            }));
        }
    }
    return [];
}
